import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import LabelIcon from "@mui/icons-material/Label";
import RefreshIcon from "@mui/icons-material/Refresh";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { ChargerConfig, ChargerGroup, CountedPage, LabelRule } from "../types";
import { useToast } from "../toast";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import PushFirmwareDialog from "../dialogs/PushFirmwareDialog";

// ── Create Group Dialog ────────────────────────────────────────────────────────

function CreateGroupDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useAuthApi();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api(`${MGMT}/charger-groups`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), description: description.trim() || undefined }),
      });
      toast.show("Group created");
      setName("");
      setDescription("");
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Charger Group</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Manage Chargers Dialog ─────────────────────────────────────────────────────

function ManageChargersDialog({
  open,
  group,
  chargers,
  onClose,
  onSaved,
}: {
  open: boolean;
  group: ChargerGroup | null;
  chargers: ChargerConfig[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useAuthApi();
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState<number | null>(null);
  const toast = useToast();

  const memberIds = new Set((group?.chargers ?? []).map((c) => c.id));
  const dynamicIds = new Set((group?.chargers ?? []).filter((c) => c.is_dynamic).map((c) => c.id));

  const filtered = chargers.filter(
    (c) =>
      c.charge_point_id.toLowerCase().includes(search.toLowerCase()) ||
      (c.display_name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const add = async (config: ChargerConfig) => {
    if (!group) return;
    setSaving(config.id);
    try {
      await api(`${MGMT}/charger-groups/${group.id}/chargers/${config.id}`, { method: "POST" });
      toast.show(`${config.display_name ?? config.charge_point_id} added`);
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(null);
    }
  };

  const remove = async (config: ChargerConfig) => {
    if (!group) return;
    setSaving(config.id);
    try {
      await api(`${MGMT}/charger-groups/${group.id}/chargers/${config.id}`, { method: "DELETE" });
      toast.show(`${config.display_name ?? config.charge_point_id} removed`);
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Chargers: "{group?.name}"</DialogTitle>
      <DialogContent>
        <TextField
          label="Search chargers"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ mb: 2, mt: 1 }}
        />
        <TableContainer>
          <Table size="small">
            <TableBody>
              {filtered.map((c) => {
                const isMember = memberIds.has(c.id);
                return (
                  <TableRow key={c.id} sx={{ bgcolor: isMember ? "action.selected" : undefined }}>
                    <TableCell sx={{ fontFamily: "monospace" }}>{c.charge_point_id}</TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>{c.display_name ?? "–"}</TableCell>
                    <TableCell>
                    {isMember && !dynamicIds.has(c.id) && (
                      <Chip label="explicit" size="small" color="primary" variant="outlined" />
                    )}
                    {dynamicIds.has(c.id) && (
                      <Chip label="dynamic" size="small" color="info" variant="outlined" />
                    )}
                    </TableCell>
                    <TableCell align="right">
                    {dynamicIds.has(c.id) ? (
                      <Button size="small" disabled title="Dynamic member via label rules">
                        Auto
                      </Button>
                    ) : isMember ? (
                      <Button size="small" color="error" onClick={() => remove(c)} disabled={saving === c.id}>
                        Remove
                      </Button>
                      ) : (
                        <Button size="small" onClick={() => add(c)} disabled={saving === c.id}>
                          Add
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function LabelRulesDialog({
  open,
  group,
  onClose,
  onSaved,
}: {
  open: boolean;
  group: ChargerGroup | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useAuthApi();
  const [rules, setRules] = useState<LabelRule[]>([]);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (open && group) {
      setRules(group.label_rules ?? []);
    }
  }, [open, group]);

  const addRule = async () => {
    if (!group || !key.trim() || !value.trim()) return;
    setSaving(true);
    try {
      const rule = await api<LabelRule>(`${MGMT}/charger-groups/${group.id}/label-rules`, {
        method: "POST",
        body: JSON.stringify({ key: key.trim(), value: value.trim() }),
      });
      if (rule) setRules((prev) => [...prev, rule]);
      setKey("");
      setValue("");
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (ruleId: number) => {
    if (!group) return;
    setDeletingId(ruleId);
    try {
      await api(`${MGMT}/charger-groups/${group.id}/label-rules/${ruleId}`, { method: "DELETE" });
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Label Rules: "{group?.name}"</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Chargers matching ALL rules below are dynamically added to this group.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
          <TextField
            label="Key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            onClick={addRule}
            disabled={saving || !key.trim() || !value.trim()}
            size="small"
          >
            Add
          </Button>
        </Stack>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {rules.length === 0 && (
            <Typography variant="body2" color="text.secondary">No label rules yet.</Typography>
          )}
          {rules.map((r) => (
            <Chip
              key={r.id}
              label={`${r.key}=${r.value}`}
              onDelete={() => deleteRule(r.id)}
              disabled={deletingId === r.id}
              color="info"
              variant="outlined"
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main View ──────────────────────────────────────────────────────────────────

export default function ChargerGroups() {
  const api = useAuthApi();
  const [groups, setGroups] = useState<ChargerGroup[]>([]);
  const [chargers, setChargers] = useState<ChargerConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ChargerGroup | null>(null);
  const [managingChargersOf, setManagingChargersOf] = useState<ChargerGroup | null>(null);
  const [managingRulesOf, setManagingRulesOf] = useState<ChargerGroup | null>(null);
  const [pushingFirmwareTo, setPushingFirmwareTo] = useState<ChargerGroup | null>(null);
  const [search, setSearch] = useState("");
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, c] = await Promise.all([
        api<ChargerGroup[]>(`${MGMT}/charger-groups`),
        api<CountedPage<ChargerConfig>>(`${MGMT}/charger-configs`),
      ]);
      setGroups(g ?? []);
      setChargers(c?.items ?? []);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // After any mutation, reload and re-sync the open dialog's group data
  const reloadAndSync = useCallback(async () => {
    const [g, c] = await Promise.all([
      api<ChargerGroup[]>(`${MGMT}/charger-groups`),
      api<CountedPage<ChargerConfig>>(`${MGMT}/charger-configs`),
    ]).catch(() => [[], null] as [ChargerGroup[], CountedPage<ChargerConfig> | null]);
    const groups = g ?? [];
    setGroups(groups);
    setChargers(c?.items ?? []);
    setManagingChargersOf((prev) => prev ? (groups.find((gr) => gr.id === prev.id) ?? null) : null);
    setManagingRulesOf((prev) => prev ? (groups.find((gr) => gr.id === prev.id) ?? null) : null);
  }, []);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(`${MGMT}/charger-groups/${deleting.id}`, { method: "DELETE" });
      toast.show("Group deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.show((e as Error).message, "error");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) =>
      [g.name, g.description].filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [groups, search]);

  const hasFilters = search.trim().length > 0;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          New Group
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Filter charger groups…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        {hasFilters && <Button size="small" onClick={() => setSearch("")}>Clear filters</Button>}
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Chargers</TableCell>
              <TableCell align="center">Label Rules</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      {hasFilters ? "No charger groups match the current filters." : "No charger groups yet."}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((g) => (
              <TableRow key={g.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{g.name}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{g.description ?? "–"}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={g.chargers.length}
                    size="small"
                    color={g.chargers.length > 0 ? "primary" : "default"}
                    variant="outlined"
                    onClick={() => setManagingChargersOf(g)}
                    clickable
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={g.label_rules?.length ?? 0}
                    size="small"
                    color={(g.label_rules?.length ?? 0) > 0 ? "info" : "default"}
                    variant="outlined"
                    onClick={() => setManagingRulesOf(g)}
                    clickable
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" title="Manage chargers" onClick={() => setManagingChargersOf(g)}>
                    <GroupAddIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Manage label rules" onClick={() => setManagingRulesOf(g)}>
                    <LabelIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" title="Push firmware" onClick={() => setPushingFirmwareTo(g)}>
                    <SystemUpdateAltIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(g)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CreateGroupDialog
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={() => { setCreating(false); load(); }}
      />

      <ManageChargersDialog
        open={managingChargersOf !== null}
        group={managingChargersOf}
        chargers={chargers}
        onClose={() => setManagingChargersOf(null)}
        onSaved={reloadAndSync}
      />

      <LabelRulesDialog
        open={managingRulesOf !== null}
        group={managingRulesOf}
        onClose={() => setManagingRulesOf(null)}
        onSaved={reloadAndSync}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete group?"
        message={`Delete charger group "${deleting?.name}"? Chargers will not be deleted.`}
        onCancel={() => setDeleting(null)}
        onConfirm={handleDelete}
      />

      <PushFirmwareDialog
        open={pushingFirmwareTo !== null}
        group={pushingFirmwareTo}
        onClose={() => setPushingFirmwareTo(null)}
      />
    </>
  );
}
